import { useEffect, useId, useRef, useState } from 'react'

import { ToastService } from '../../../services/toast.service'
import { CancelConfirmDialog } from '../cancel-confirm-dialog'
import {
  DEFAULT_CANCEL_CONFIRM_MESSAGE,
  DEFAULT_CANCEL_CONFIRM_NO_LABEL,
  DEFAULT_CANCEL_CONFIRM_YES_LABEL,
  DEFAULT_CANCEL_LABEL,
  DEFAULT_CHAR_LIMIT,
  DEFAULT_COMMENT_HELP_TEXT,
  DEFAULT_COMMENT_REQUIRED_ERROR,
  DEFAULT_FILE_HELP_TEXT,
  DEFAULT_FILE_SIZE_ERROR,
  DEFAULT_FILE_TYPE_ERROR,
  DEFAULT_NO_FILE_SELECTED_LABEL,
  DEFAULT_POPUP_TITLE,
  DEFAULT_SELECT_FILE_LABEL,
  DEFAULT_SHARE_LABEL,
  DEFAULT_SUBMIT_ERROR_MESSAGE,
  DEFAULT_SUBMIT_SUCCESS_MESSAGE,
  DEFAULT_TEXTAREA_PLACEHOLDER,
} from '../constants/labels'
import { addCaseComment } from '../services/CopCaseDetails.service'

const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'mp4', 'pdf', 'docx', 'txt', 'xlsx', 'csv', 'pptx', 'zip'])
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024

export type CaseAddCommentModalProps = {
  caseId?: string
  title?: string
  commentHelpText?: string
  textareaPlaceholder?: string
  charLimit?: number
  fileHelpText?: string
  cancelLabel?: string
  shareLabel?: string

  commentRequiredError?: string

  fileTypeError?: string

  fileSizeErrorMessage?: string

  submitSuccessMessage?: string

  submitErrorMessage?: string

  selectFileLabel?: string

  noFileSelectedLabel?: string
  onClose: () => void

  onCommentAdded?: () => void

  cancelConfirmMessage?: string

  cancelConfirmYesLabel?: string

  cancelConfirmNoLabel?: string

  cancelConfirmSubtext?: string
}

export default function CaseAddCommentModal({
  caseId,
  title = DEFAULT_POPUP_TITLE,
  commentHelpText = DEFAULT_COMMENT_HELP_TEXT,
  textareaPlaceholder = DEFAULT_TEXTAREA_PLACEHOLDER,
  charLimit = DEFAULT_CHAR_LIMIT,
  fileHelpText = DEFAULT_FILE_HELP_TEXT,
  cancelLabel = DEFAULT_CANCEL_LABEL,
  shareLabel = DEFAULT_SHARE_LABEL,
  commentRequiredError = DEFAULT_COMMENT_REQUIRED_ERROR,
  fileTypeError = DEFAULT_FILE_TYPE_ERROR,
  fileSizeErrorMessage = DEFAULT_FILE_SIZE_ERROR,
  submitSuccessMessage = DEFAULT_SUBMIT_SUCCESS_MESSAGE,
  submitErrorMessage = DEFAULT_SUBMIT_ERROR_MESSAGE,
  selectFileLabel = DEFAULT_SELECT_FILE_LABEL,
  noFileSelectedLabel = DEFAULT_NO_FILE_SELECTED_LABEL,
  onClose,
  onCommentAdded,
  cancelConfirmMessage = DEFAULT_CANCEL_CONFIRM_MESSAGE,
  cancelConfirmYesLabel = DEFAULT_CANCEL_CONFIRM_YES_LABEL,
  cancelConfirmNoLabel = DEFAULT_CANCEL_CONFIRM_NO_LABEL,
  cancelConfirmSubtext,
}: Readonly<CaseAddCommentModalProps>) {
  const modalTitleId = useId()
  const commentHelpId = useId()
  const commentErrorId = useId()
  const charCountId = useId()
  const fileHelpId = useId()
  const fileErrorId = useId()
  const [comment, setComment] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState('')
  const [commentError, setCommentError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCancelConfirm) {
          setShowCancelConfirm(false)
        } else if (comment.trim() || selectedFile) {
          setShowCancelConfirm(true)
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, showCancelConfirm, comment, selectedFile])

  const validateAndSetFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      setFileError(fileTypeError)
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(fileSizeErrorMessage)
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setFileError('')
    setSelectedFile(file)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) validateAndSetFile(file)
  }

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    if (val.length <= charLimit) {
      setComment(val)
      if (val.trim()) setCommentError('')
    }
  }

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setCommentError(commentRequiredError)
      return
    }
    setCommentError('')
    setIsSubmitting(true)
    try {
      await addCaseComment(caseId ?? '', comment, selectedFile ?? undefined)
      onCommentAdded?.()
      ToastService.success(submitSuccessMessage)
      onClose()
    } catch {
      ToastService.error(submitErrorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const commentDescribedBy = [
    commentHelpText ? commentHelpId : '',
    charCountId,
    commentError ? commentErrorId : '',
  ].filter(Boolean).join(' ')

  const fileInputDescribedBy = [
    fileHelpText ? fileHelpId : '',
    fileError ? fileErrorId : '',
  ].filter(Boolean).join(' ')

  return (
    <dialog className="cop-modal-overlay" aria-modal="true" aria-labelledby={modalTitleId}>
      <div className="cop-modal">

        <div className="cop-modal__header">
          <h2 id={modalTitleId} className="cop-modal__title">{title}</h2>
          <button type="button" className="cop-modal__close" aria-label="Close" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {commentHelpText && (
          <p id={commentHelpId} className="cop-modal__help-text">{commentHelpText}</p>
        )}

        <div className="cop-modal__textarea-wrap">
          <textarea
            className={`cop-modal__textarea${commentError ? ' cop-modal__textarea--error' : ''}`}
            placeholder={textareaPlaceholder}
            value={comment}
            onChange={handleCommentChange}
            rows={6}
            maxLength={charLimit}
            aria-required="true"
            aria-invalid={Boolean(commentError)}
            aria-describedby={commentDescribedBy}
          />
          <span id={charCountId} className="cop-modal__char-count">{comment.length}/{charLimit}</span>
        </div>
        {commentError && (
          <p id={commentErrorId} className="cop-modal__validation-error" role="alert">{commentError}</p>
        )}

        <div className="cop-modal__file-row">
          <button
            type="button"
            className="cop-modal__file-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            {selectFileLabel}
          </button>
          <span className={`cop-modal__file-name${selectedFile ? ' cop-modal__file-name--selected' : ''}`}>
            {selectedFile ? selectedFile.name : noFileSelectedLabel}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            className="cop-modal__file-input"
            aria-label="Select file"
            aria-describedby={fileInputDescribedBy || undefined}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png,.mp4,.pdf,.docx,.txt,.xlsx,.csv,.pptx,.zip"
          />
        </div>

        {fileHelpText && (
          <p id={fileHelpId} className="cop-modal__file-help">{fileHelpText}</p>
        )}

        {fileError && (
          <p id={fileErrorId} className="cop-modal__validation-error" role="alert">{fileError}</p>
        )}

        <div className="cop-modal__footer">
          <button
            type="button"
            className="cop-modal__btn cop-modal__btn--share"
            onClick={handleSubmit}
            disabled={isSubmitting || !comment.trim()}
          >
            {isSubmitting ? '…' : shareLabel}
          </button>
          <button type="button" className="cop-modal__btn cop-modal__btn--cancel" onClick={() => comment.trim() ? setShowCancelConfirm(true) : onClose()}>
            {cancelLabel}
          </button>
        </div>

      </div>

      {showCancelConfirm && (
        <CancelConfirmDialog
          message={cancelConfirmMessage}
          yesLabel={cancelConfirmYesLabel}
          noLabel={cancelConfirmNoLabel}
          subtext={cancelConfirmSubtext}
          onConfirm={onClose}
          onDismiss={() => setShowCancelConfirm(false)}
        />
      )}
    </dialog>
  )
}
